'use client'
import { cn } from '@/utilities/ui'
import useClickableCard from '@/utilities/useClickableCard'
import Link from 'next/link'
import React from 'react'

import type { Post } from '@/payload-types'

import { Media } from '@/components/Media'

export type CardPostData = Pick<Post, 'slug' | 'categories' | 'meta' | 'title' | 'publishedAt' | 'heroImage' | 'description'>

export const Card: React.FC<{
  alignItems?: 'center'
  className?: string
  doc?: CardPostData
  relationTo?: 'posts'
  showCategories?: boolean
  featured?: boolean
  title?: string
}> = (props) => {
  const { card, link } = useClickableCard({})
  const { className, doc, relationTo, featured, title: titleFromProps } = props

  const { slug, meta, title, publishedAt, heroImage, description: postDescription } = doc || {}
  const { description: metaDescription, image: metaImage } = meta || {}
  const description = postDescription || metaDescription

  // const hasCategories = categories && Array.isArray(categories) && categories.length > 0
  const titleToUse = titleFromProps || title
  const sanitizedDescription = description?.replace(/\s/g, ' ') // replace non-breaking space with white space
  const href = `/${relationTo}/${slug}`

  // Format date
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  return (
    <article
      className={cn(
        'group relative overflow-hidden bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 hover:cursor-pointer',
        featured && 'h-full',
        className,
      )}
      ref={card.ref}
    >
      <div className={cn("relative w-full", featured ? "h-96" : "h-64")}>
        <div className="absolute inset-0 bg-gray-100">
          {heroImage && typeof heroImage !== 'string' && (
            <Media resource={heroImage} size={featured ? "50vw" : "33vw"} className="w-full h-full object-cover" />
          )}
          {!heroImage && metaImage && typeof metaImage !== 'string' && (
            <Media resource={metaImage} size={featured ? "50vw" : "33vw"} className="w-full h-full object-cover" />
          )}
          {!heroImage && !metaImage && (
            <div className="w-full h-full flex items-center justify-center">
              <svg viewBox="0 0 400 300" className="w-full h-full p-12">
                {/* Simple cleaning illustration */}
                <rect x="50" y="200" width="300" height="10" fill="#D1D5DB" />
                <ellipse cx="200" cy="170" rx="40" ry="20" fill="#0075FF" />
                <rect x="230" y="130" width="5" height="50" fill="#211F54" transform="rotate(-30 232 155)" />
                <circle cx="250" cy="120" r="15" fill="#211F54" />
                <text x="140" y="140" fill="#0075FF" fontSize="20">✨</text>
                <text x="260" y="160" fill="#0075FF" fontSize="16">✨</text>
              </svg>
            </div>
          )}
        </div>
      </div>
      
      <div className="p-6">
        {titleToUse && (
          <h3 className={cn(
            "font-bold text-[#211F54] group-hover:text-[#0075FF] transition-colors",
            featured ? "text-2xl mb-3" : "text-xl mb-2"
          )}>
            <Link href={href} ref={link.ref} className="after:absolute after:inset-0">
              {titleToUse}
            </Link>
          </h3>
        )}
        
        {description && (
          <p className="text-gray-600 line-clamp-2 mb-4">
            {sanitizedDescription}
          </p>
        )}
        
        <div className="flex items-center justify-between">
          {publishedAt && (
            <time className="text-sm text-gray-500">
              {formatDate(publishedAt)}
            </time>
          )}
          
          <div className="ml-auto">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#0075FF] text-white group-hover:bg-[#0066E6] transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </article>
  )
}
